import React from 'react';
import { LazyAtlassianNavigation } from '@atlassian/jira-atlassian-navigation/src/async.tsx';
import { coreProjectLayout } from '@atlassian/jira-core-project-layout/src/index.tsx';
import { MENU_ID, SIDEBAR_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { forgeProjectModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import AsyncSidebarCore from '@atlassian/jira-navigation-apps-sidebar-core/src/async.tsx';
import { createEntry } from '@atlassian/jira-route-entry/src/createEntry.tsx';
import { getBusinessProjectResources } from '@atlassian/jira-router-resources-business-common/src/index.tsx';
import { getNavigationResources } from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { jiraSpaResources } from '@atlassian/jira-router-resources-spa-page-container/src/index.tsx';
import { rovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';
import { projectSettingsCoreIssueTypesWorkflowRoute } from '@atlassian/jira-router-routes-project-settings-core-routes/src/projectSettingsCoreIssueTypesWorkflowRoute.tsx';
import type WorkflowNavigationSettingsType from '@atlassian/jira-spa-apps-project-settings-workflow-navigation/src/index.tsx';
import { markAsProductBundleReady } from '@atlassian/jira-spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@atlassian/jira-spa/src/view/lazy-page/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { licenseDetailsResource_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-router-resources-business-license-details/src/index.tsx';
import { chromelessLayout } from '@atlassian/jira-chromeless-layout/src/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { workflowBuilderProvisioningResource } from '@atlassian/jira-router-resources-admin-pages-workflow-builder-provisioning/src/index.tsx';
import { getNavigationSidebarProjectSettingsResource } from './common/getNavigationSidebarProjectSettingsResource.tsx';

const LazyWorkflowNavigationSettings = lazyForPaint<typeof WorkflowNavigationSettingsType>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-project-settings-workflow-navigation", jiraSpaEntry: "async-project-settings-workflow-navigation" */ '@atlassian/jira-spa-apps-project-settings-workflow-navigation'
		),
	),
);

const WorkflowNavigationPage = () => (
	<LazyPage
		Page={LazyWorkflowNavigationSettings}
		pageId="project-settings-workflow-navigation"
		shouldShowSpinner={false}
	/>
);

const getWorkflowBuilderProvisioningResources = () => [workflowBuilderProvisioningResource];

export const projectSettingsCoreIssueTypesWorkflowRouteEntry = createEntry(
	projectSettingsCoreIssueTypesWorkflowRoute,
	() => ({
		group: 'project-settings-core',

		navigation: expValEquals(
			'hix-7127_wfe_layout_to_chromeless_for_business',
			'isEnabled',
			true,
			false,
		)
			? undefined
			: {
					menuId: MENU_ID.PROJECTS,
					// delete when cleaning up getWillShowNav4
					sidebar: AsyncSidebarCore,
					sidebarId: SIDEBAR_ID.PROJECT_SETTINGS,
				},

		layout: expValEquals('hix-7127_wfe_layout_to_chromeless_for_business', 'isEnabled', true, false)
			? chromelessLayout
			: coreProjectLayout,

		resources: expValEquals(
			'hix-7127_wfe_layout_to_chromeless_for_business',
			'isEnabled',
			true,
			false,
		)
			? [
					projectContextResource,
					...jiraSpaResources(),
					rovoEntitlementResource,
					...getWorkflowBuilderProvisioningResources(),
				]
			: [
					...getNavigationResources(),
					...getWorkflowBuilderProvisioningResources(),
					// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
					projectContextResource,
					forgeProjectModuleResource,
					staticSidebarShortcutsResource,
					staticProjectSidebarNavigationResource,
					staticProjectSidebarConnectAddonsNavigationResource,
					// end of resources expanded from getNavigationSidebarProjectResource
					...getNavigationSidebarProjectSettingsResource(),
					...getBusinessProjectResources(),
					licenseDetailsResource_DEPRECATED_DO_NOT_USE,
				],

		component: WorkflowNavigationPage,

		forPaint: expValEquals(
			'hix-7127_wfe_layout_to_chromeless_for_business',
			'isEnabled',
			true,
			false,
		)
			? [LazyWorkflowNavigationSettings]
			: [LazyAtlassianNavigation, LazyWorkflowNavigationSettings],

		bifrost: {
			bifrostGroup: 'shell-only',

			bundling: {
				packageName:
					'@atlassian/jira-router-routes-project-settings-core-entries/src/projectSettingsCoreIssueTypesWorkflowRouteEntry.tsx',
				whichExportToUse: 'projectSettingsCoreIssueTypesWorkflowRouteEntry',
			},

			enabled: true,
		},
	}),
);
